import React, { useState, useEffect } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import { Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import EditNoteIcon from '@mui/icons-material/ModeEditOutlineTwoTone';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useNavigate } from 'react-router-dom';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { toast } from 'react-toastify';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import Loading from '../../loading';
import { RootState, resetAllStores } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { SystemUserService } from '../../../services/system-user.service';
import { ADMIN_COLUMNS } from '../../../builders/admins-builders';
import { Label } from '../../label';
import { AdminDetailsDrawer } from '../../details-drawers/admin-details-drawer';
import { createSystemUserPermissionCheck, permissionCheck, returnRoleFromPermissions } from '../../../helpers/permissionCheck';
import { customerPermissionFlags, permissionToText } from '../../../constants/systemPermissions.constants';
import { SystemUserTypes } from '../../../types';
import AreYouSureModal from '../../are-you-sure-modal';
import { useGenericMutation } from '../../../services/react-query/generic.mutation';
import { handleApiError, queryClient } from '../../../services/react-query/react-query.client';
import { AuthService } from '../../../services/auth.service';
import OptionsDrawer, { DrawerOption } from '../../options-drawer';
import { Autorenew } from '@mui/icons-material';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import { SystemUserListFilters } from './system-users-filters';
import NoData from '../../no-data';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import { Team } from '../../../types/teams.types';
import { publishSessionSyncEvent, SESSION_SYNC_ACTIONS } from '../../../services/session-sync.service';

export const SystemUserList: React.FC = () => {
    const navigate = useNavigate();
    const { authUser } = useSelector((state: RootState) => state.auth);
    // A POM Master in their own session sees every customer's system users;
    // the only action that matters there is masquerading, so a row click goes
    // straight to the masquerade confirmation instead of the options drawer.
    const isPomMasterView = Boolean(authUser?.pomMaster && !authUser?.masquarade);
    const showCustomerColumn = isPomMasterView;
    const [options, setOptions] = useState<DrawerOption[]>([]);
    const [toggleActiveModal, setToggleActiveModal] = useState(false);
    const { 
        mutateAsync: toggleActiveStatus, 
        isLoading: toggleActiveStatusLoading,
    } = useGenericMutation<{ id: string }, boolean>('toggleActiveStatus', `customerWebAppUser/toggleActiveStatus`, 'post');

    const { mutateAsync: handleMasquarade, isLoading: masquaradeLoading } =
        useGenericMutation<{ id: string }, boolean>(
            'masquaradeUser',
            `customerWebAppUser/masquarade`,
            'post'
        );
    const { mutateAsync: deleteUser, isLoading: deleteLoading } =
        useGenericMutation<{ id: string }, boolean>(
            'deleteUser',
            `customerWebAppUser/deleteWebAppUser`,
            'post'
        );

    const { mutateAsync: resendInvite } =
        useGenericMutation<{ userId: string }, boolean>(
            'resendInvite',
            `customerWebAppUser/resendInvite`,
            'post'
        );
    const [optionsDrawer, setOptionsDrawer] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState({} as SystemUserTypes.Model);
    const [modal, setModal] = useState({
        details: false,
        masquarade: false,
    });
    const { pagination: { total, items }, loading } = useSelector((state: RootState) => state.systemUser);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });
    const [activeFilters, setActiveFilters] = useState<SystemUserTypes.Filters>({} as SystemUserTypes.Filters);

    const toggleModal = (id: keyof typeof modal, v: boolean) => setModal((m) => ({ ...m, [id]: v }));

    const applyFilters = async (
        filters: SystemUserTypes.Filters,
        targetPagination: PaginatorFactory<any> = pagination,
    ) => {
        const { teams: teamFilter, ...backendFilters } = filters;
        const normalizedTeamFilter = teamFilter?.trim().toLowerCase();
        const shouldFilterByTeam = Boolean(normalizedTeamFilter);

        if (shouldFilterByTeam && !teams) {
            toast('Teams are still loading, please try again.', { type: 'warning' });
            return;
        }

        try {
            SystemUserService.dispatch.toggleLoading('fetchMany', true);

            const result = await SystemUserService.fetchMany({
                ...targetPagination,
                page: targetPagination.page + 1,
                filter: { ...backendFilters },
            }, undefined, true);

            if (!result) {
                return;
            }

            if (!shouldFilterByTeam) {
                SystemUserService.dispatch.setPagination(result);
                return;
            }

            const filteredItems = result.items?.filter((admin) => {
                const userTeams = teams!.filter((team) => team.leadersIds?.includes(admin.id));
                const teamNames = userTeams.map((team) => team.name || '').filter(Boolean);

                return teamNames.some((teamName) =>
                    teamName.toLowerCase().includes(normalizedTeamFilter!)
                );
            }) || [];

            SystemUserService.dispatch.setPagination({
                ...result,
                items: filteredItems,
            });
        } catch (error) {
            const msg = handleApiError(error) || 'Failed to fetch system users.';
            toast(msg, {
                autoClose: 1500,
                type: 'error',
            });
        } finally {
            SystemUserService.dispatch.toggleLoading('fetchMany', false);
        }
    }

    const onFilter = async (filters: SystemUserTypes.Filters) => {
        setActiveFilters(filters);
        await applyFilters(filters);
    }

    const makeCsvDownload = async () => {
        // Honor the currently-applied filters. Send backend-fillable filters
        // to the API (fullname / email / customerId), then apply the same
        // client-side team post-filter applyFilters uses — otherwise an
        // export from a filtered view silently returned the entire org.
        const { teams: teamFilter, ...backendFilters } = activeFilters;
        const normalizedTeamFilter = teamFilter?.trim().toLowerCase();
        const shouldFilterByTeam = Boolean(normalizedTeamFilter);

        // Mirror applyFilters' guard — without teams loaded we can't compute
        // the client-side post-filter, and silently skipping it would export
        // the full org while the list view shows a team-filtered subset.
        if (shouldFilterByTeam && !teams) {
            toast('Teams are still loading, please try again.', { type: 'warning' });
            return;
        }

        try {
            const res: PaginationFactory<SystemUserTypes.Model> = await SystemUserService.fetchMany(
                { perPage: 100000, page: 1, filter: { ...backendFilters } },
                undefined,
                true,
            ) as PaginationFactory<SystemUserTypes.Model>;

            let rows = res.items ?? [];
            if (shouldFilterByTeam) {
                rows = rows.filter((admin) => {
                    const userTeams = teams!.filter((team) => team.leadersIds?.includes(admin.id));
                    const teamNames = userTeams.map((t) => t.name || '').filter(Boolean);
                    return teamNames.some((name) => name.toLowerCase().includes(normalizedTeamFilter!));
                });
            }

            const csvArray = rows.map((user) => ({
                userRole: permissionToText[returnRoleFromPermissions(user?.permissionFlags || {})] ?? '',
                email: user?.email ?? '',
                userName: user?.fullname ?? '',
                status: user?.active ? 'enabled' : 'disabled',
                createdAt: user?.createdAt ? dayjs(user?.createdAt).format('MM/DD/YYYY hh:mm A') : 'N/A',
                lastLogin: user?.lastActive ? dayjs(user?.lastActive).format('MM/DD/YYYY hh:mm A') : 'N/A',
            }));

            downloadCsv(csvArray, 'pom-system-users');
        } catch (error) {
            const msg = handleApiError(error) || 'Failed to export system users.';
            toast(msg, { autoClose: 1500, type: 'error' });
        }
    }

    useEffect(() => {
        void applyFilters(activeFilters, pagination);
    }, [pagination?.perPage, pagination?.page]);

    useEffect(() => {
        const opts: DrawerOption[] = [{
            icon: <ManageSearchIcon />,
            label: 'View System User Details',
            onClick: () => {
                setOptionsDrawer(false);
                toggleModal('details', true);
            }
        }];

        // No Masquerade entry here: the drawer never opens in the POM Master
        // view (row click confirms masquerade directly), and nobody else may
        // masquerade.

        if (selectedAdmin?.isFirstLogin && permissionCheck(customerPermissionFlags.ADMIN)) {
            opts.push({
                icon: <Autorenew />,
                label: 'Resend invite',
                onClick: async () => {
                   
                    await resendInvite({ userId: selectedAdmin?.id }, {
                        onError: (e) => {
                            const msg = handleApiError(e);

                            toast(msg, {
                                autoClose: 1500,
                                type: 'error',
                            });
                        },
                        onSuccess: () => {
                            toast('Successfully resent invite.', {
                                autoClose: 1500,
                                type: 'success',
                            });
                        },
                    });
                }
            });
        }

        if (permissionCheck(customerPermissionFlags.TEAM_LEADER)) {
            opts.push({
                icon: <EditNoteIcon />,
                label: 'Edit',
                onClick: () => {
                    setOptionsDrawer(false);
                    navigate(`/system-users/${selectedAdmin?.id}/edit`);
                }
            });
        }

        if ((selectedAdmin?.active) && createSystemUserPermissionCheck(customerPermissionFlags.ADMIN)) {
            opts.push({
                icon: <ToggleOffIcon />,
                label: 'Disable',
                onClick: () => {
                    setOptionsDrawer(false);
                    setToggleActiveModal(true);
                }
            });
        }

        if (!selectedAdmin?.active && createSystemUserPermissionCheck(customerPermissionFlags.ADMIN)) {
            opts.push({
                icon: <ToggleOnIcon />,
                label: 'Enable',
                onClick: () => {
                    setOptionsDrawer(false);
                    setToggleActiveModal(true);
                }
            });
        }

        if (permissionCheck(customerPermissionFlags.ADMIN)) {
            opts.push({
                icon: <DeleteForeverIcon />,
                label: 'Delete permanently',
                onClick: () => {
                    setOptionsDrawer(false);
                    setDeleteModal(true);
                }
            })
        }

        setOptions(opts);
    }, [selectedAdmin]);

    const { data: teams, isLoading : isTeamsLoading } = useGenericQuery<Team[]>(
        'fetchAllTeams',
        'teams/fetchAllTeams',
        0,
        0,
        false
    );

    // Helper to get team names for a system user
    const getTeamNames = (adminId: string) => {
        if (isTeamsLoading) return 'Loading...';
        if (!teams) return 'N/A';
        const userTeams = teams.filter(team => team.leadersIds?.includes(adminId));
        if (!userTeams.length) return 'N/A';
        return userTeams.map(t => t.name || '').filter(Boolean).join(', ');
    };

    const selectedAwaitingSetup = Boolean(selectedAdmin?.isFirstLogin);
    const selectedAdminLabel = selectedAdmin?.fullname
        ? `${selectedAdmin.fullname}${selectedAdmin?.email ? ` (${selectedAdmin.email})` : ''}`
        : (selectedAdmin?.email || 'this user');

    const displayedColumns = showCustomerColumn
        ? ADMIN_COLUMNS
        : ADMIN_COLUMNS.filter((column) => column.field !== 'customerName');

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <SystemUserListFilters 
                    onFilter={onFilter}
                    onDownload={async () => {
                        await makeCsvDownload();
                    }}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {displayedColumns?.map((column) => (
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading?.fetchMany}>
                            <TableBody>
                                {items?.map((admin) => (
                                    <TableRow 
                                        key={admin?.id} 
                                        onClick={() => {
                                            setSelectedAdmin(admin);
                                            if (isPomMasterView) {
                                                toggleModal('masquarade', true);
                                            } else {
                                                setOptionsDrawer(true);
                                            }
                                        }}
                                        sx={{ 
                                            ":hover": { 
                                                backgroundColor: '#F0F0F0',
                                                cursor: 'pointer'
                                            }}
                                        } 
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {permissionToText[returnRoleFromPermissions(admin?.permissionFlags || {})]}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {admin?.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {admin?.fullname}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {showCustomerColumn && (
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {admin?.customerName || admin?.customer?.name || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                                                {getTeamNames(admin.id)}
                                            </Typography>
                                        </TableCell>
                                        
                                        <TableCell>
                                            {
                                                admin?.isFirstLogin ?  
                                                <Label
                                                    variant={'ghost'}
                                                    color={'warning'}
                                                >
                                                    Awaiting Setup
                                                </Label> :
                                                <Label
                                                    variant={'ghost'}
                                                    color={'success'}
                                                >
                                                    Setup Completed
                                                </Label>
                                            }
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {admin?.active ? 'Enabled' : 'Disabled'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {admin?.createdAt ? dayjs(admin?.createdAt).format('MM/DD/YYYY hh:mm A') : '----'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {admin?.lastActive ? dayjs(admin?.lastActive).format('MM/DD/YYYY hh:mm A') : '----'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>

                    <When
                        condition={
                            loading.fetchMany === false && items?.length < 1
                        }
                    >
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No records found'}
                            description={
                                'There are no system users registered in your subscription.'
                            }
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, perPage: parseInt(e.target.value, 10) }))}
                />
            </Card>

            <OptionsDrawer 
                anchor="bottom"
                onClose={() => {
                    setOptionsDrawer(false);
                }}
                open={optionsDrawer}
                options={options}
            />

            <AdminDetailsDrawer
                admin={selectedAdmin}
                visible={modal.details}
                onClose={() => {
                    toggleModal('details', false);
                    setSelectedAdmin({} as SystemUserTypes.Model);
                }}
            />

            <AreYouSureModal
                visible={deleteModal}
                title={'Delete System User'}
                description={`Are you sure you want to delete this user? This action is permanent.`}
                onHide={() => {
                    setDeleteModal(false);
                    setSelectedAdmin({} as SystemUserTypes.Model);
                }}
                onConfirm={async () => {
                    await deleteUser({ id: selectedAdmin?.id }, {
                        onError: (e) => {
                            setDeleteModal(false);
                            setSelectedAdmin({} as SystemUserTypes.Model);

                            const msg = handleApiError(e);

                            toast(msg, {
                                autoClose: 1500,
                                type: 'error',
                            });
                        },
                        onSuccess: async () => {
                            setDeleteModal(false);
                            setSelectedAdmin({} as SystemUserTypes.Model);
                            await SystemUserService.fetchMany({ ...pagination, page: pagination.page + 1, filter: { } });

                            toast('Successfully deleted user.', {
                                autoClose: 1500,
                                type: 'success',
                            });
                        },
                    });
                }}
                loading={deleteLoading}
            />

            <AreYouSureModal 
                visible={toggleActiveModal}
                title={selectedAdmin?.active ? 'Disable System User' : 'Enable System User'}
                description={`Are you sure you want to ${selectedAdmin?.active ? 'disable' : 'enable'} this user?`}
                onHide={() => {
                    setToggleActiveModal(false);
                    setSelectedAdmin({} as SystemUserTypes.Model);
                }}
                onConfirm={async () => {
                    await toggleActiveStatus({ id: selectedAdmin?.id }, {
                        onError: (e) => {
                            setToggleActiveModal(false);
                            setSelectedAdmin({} as SystemUserTypes.Model);

                            const msg = handleApiError(e);

                            toast(msg, {
                                autoClose: 1500,
                                type: 'error',
                            });
                        },
                        onSuccess: async () => {
                            setToggleActiveModal(false);
                            setSelectedAdmin({} as SystemUserTypes.Model);
                            await SystemUserService.fetchMany({ ...pagination, page: pagination.page + 1, filter: { } });

                            toast('Successfully updated user status.', {
                                autoClose: 1500,
                                type: 'success',
                            });
                        },
                    });
                }}
                loading={toggleActiveStatusLoading}
            />

            {/* ClickUp 868m3frw9: a user still on first login has no session to
                step into (the API refuses too), so the dialog becomes a notice. */}
            <AreYouSureModal 
                visible={modal.masquarade}
                title={selectedAwaitingSetup ? 'Masquerade Not Available' : 'Masquerade User'}
                description={selectedAwaitingSetup
                    ? `${selectedAdminLabel} has not completed their account setup yet. Masquerade becomes available after their first login.`
                    : `Masquerade as ${selectedAdminLabel}? You will see the Safety Hub exactly as they do until you stop masquerading.`}
                hideConfirm={selectedAwaitingSetup}
                cancelLabel={selectedAwaitingSetup ? 'Close' : 'Cancel'}
                onHide={() => {
                    toggleModal('masquarade', false);
                    setSelectedAdmin({} as SystemUserTypes.Model);
                }}
                onConfirm={async () => {
                    await handleMasquarade({ id: selectedAdmin?.id }, {
                        onError: (e) => {
                            toggleModal('masquarade', false);
                            setSelectedAdmin({} as SystemUserTypes.Model);

                            const msg = handleApiError(e);

                            toast(msg, {
                                autoClose: 1500,
                                type: 'error',
                            });
                        },
                        onSuccess: async () => {
                            toggleModal('masquarade', false);
                            setSelectedAdmin({} as SystemUserTypes.Model);

                            // Reset all subscription-scoped stores to prevent
                            // cross-subscription data leakage during masquerade
                            resetAllStores();
                            queryClient.clear();

                            await AuthService.whoIam();
                            publishSessionSyncEvent(SESSION_SYNC_ACTIONS.MASQUERADE_CONTEXT_CHANGED);
                            navigate('/');

                            toast('Successfully masqueraded user.', {
                                autoClose: 1500,
                                type: 'success',
                            });
                        },
                    });
                }}
                color={'primary'}
                loading={masquaradeLoading}
            />
        </>
    )
}
